function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

const DEFAULT_INCLUDED_FEATURES = [
  'Live Classes',
  'Recorded video lectures',
  'Notes & PDFs',
  'Doubts Solving (Chat / Live)',
  'Exam Notifications & Alerts',
];

function parseSubscriptionFeatures(sub) {
  const plan = sub?.plan || {};
  const html = plan.description || sub.plan_description || sub.description || '';

  if (typeof document !== 'undefined' && html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    const items = Array.from(div.querySelectorAll('li'))
      .map((li) => li.textContent?.trim())
      .filter(Boolean);
    if (items.length) return items;
    const text = div.textContent?.trim();
    if (text) return [text];
  } else if (html) {
    const items = html
      .replace(/<[^>]*>/g, '\n')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (items.length) return items;
  }

  if (Array.isArray(sub.features) && sub.features.length) {
    return sub.features.map((item) => (typeof item === 'string' ? item : item?.title || item?.name)).filter(Boolean);
  }

  return DEFAULT_INCLUDED_FEATURES;
}

function formatPaymentDate(dateInput) {
  const date = parseDate(dateInput);
  if (!date) return '—';

  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function normalizeSubjectNames(sub) {
  const plan = sub?.plan || {};
  const rawSubjects =
    sub.subjects ||
    sub.active_subjects ||
    plan.subjects ||
    sub.subject_names ||
    sub.subject_name ||
    [];

  if (typeof rawSubjects === 'string') return rawSubjects;

  if (Array.isArray(rawSubjects)) {
    return rawSubjects
      .map((item) => {
        if (typeof item === 'string') return item;
        return item?.name || item?.subject_name || item?.title || '';
      })
      .filter(Boolean)
      .join(', ');
  }

  return '';
}

export function formatOrdinalDate(dateInput) {
  const date = parseDate(dateInput);
  if (!date) return '';

  const day = date.getDate();
  const ordinals = ['th', 'st', 'nd', 'rd'];
  const mod = day % 100;
  const suffix = ordinals[(mod - 20) % 10] || ordinals[mod] || ordinals[0];
  const month = date.toLocaleString('en-IN', { month: 'long' });

  return `${day}${suffix} ${month} ${date.getFullYear()}`;
}

export function getDaysRemaining(endDate) {
  const end = parseDate(endDate);
  if (!end) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  return Math.max(Math.ceil((end - today) / (1000 * 60 * 60 * 24)), 0);
}

export function getRemainingProgress(sub) {
  const start = parseDate(sub?.starts_at || sub?.start_date || sub?.started_at || sub?.created_at);
  const end = parseDate(sub?.expires_at || sub?.end_date || sub?.valid_till);
  if (!end) return 0;

  const now = new Date();
  const remainingMs = end - now;

  if (!start || end <= start) {
    return remainingMs > 0 ? 0.75 : 0;
  }

  const totalMs = end - start;
  if (totalMs <= 0) return 0;

  return Math.min(Math.max(remainingMs / totalMs, 0), 1);
}

export function isActiveUserSubscription(sub) {
  if (!sub) return false;

  if (sub.expired === true || sub.active === false) return false;

  const end = parseDate(sub.expires_at || sub.end_date || sub.valid_till);
  if (end && end < new Date()) return false;

  if (sub.status === 'expired') return false;

  return (
    sub.active === true ||
    sub.status === 'active' ||
    (sub.is_subscription === true && sub.status === 'completed' && sub.active !== false)
  );
}

export function isExpiredUserSubscription(sub) {
  if (!sub) return false;
  if (sub.expired === true) return true;
  if (isActiveUserSubscription(sub)) return false;

  const end = parseDate(sub.expires_at || sub.end_date || sub.valid_till);
  if (end && end < new Date()) return true;

  return sub.status === 'expired' || sub.active === false;
}

function formatPaymentMethod(value) {
  if (!value) return '—';
  const method = String(value).trim();
  if (!method) return '—';
  if (method.toLowerCase() === 'upi') return 'UPI';
  return method
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function mapUserSubscription(sub) {
  const active = isActiveUserSubscription(sub);
  const expired = isExpiredUserSubscription(sub);
  const endDate = sub.expires_at || sub.end_date || sub.valid_till;
  const categoryName =
    sub.class_name ||
    sub.class?.name ||
    sub.plan?.class?.name ||
    sub.plan?.category_name ||
    sub.category_name ||
    '';
  const planTitle =
    sub.plan?.plan_title ||
    sub.plan?.title ||
    sub.plan_title ||
    sub.plan_name ||
    'Plan';
  const title = categoryName ? `${categoryName} - ${planTitle}` : planTitle;

  return {
    id: sub.id,
    title,
    active,
    expired,
    endDate,
    daysRemaining: active ? getDaysRemaining(endDate) : 0,
    remainingProgress: active ? getRemainingProgress(sub) : 0,
    validLabel: active
      ? `Valid till ${formatOrdinalDate(endDate)}`
      : `Expired on ${formatOrdinalDate(endDate)}`,
    daysLabel: active
      ? `${getDaysRemaining(endDate)} Days Remaining`
      : '0 Days Remaining',
  };
}

export function mapUserSubscriptions(subscriptions = []) {
  return subscriptions
    .map(mapUserSubscription)
    .sort((a, b) => {
      if (a.active !== b.active) return a.active ? -1 : 1;
      return 0;
    });
}

export function mapSubscriptionDetails(sub) {
  if (!sub) return null;

  const summary = mapUserSubscription(sub);
  const plan = sub.plan || {};
  const payment = sub.payment || {};
  const categoryName =
    sub.class_name ||
    sub.class?.name ||
    plan.class?.name ||
    plan.class_name ||
    plan.category_name ||
    sub.category_name ||
    '';
  const planTitle = plan.plan_title || plan.title || sub.plan_title || sub.plan_name || plan.name || 'Plan';
  const classLabel = categoryName || '—';
  const stream =
    sub.subcategory_name ||
    plan.subcategory_name ||
    sub.stream_name ||
    sub.stream ||
    plan.stream_name ||
    '—';
  const subjects = normalizeSubjectNames(sub) || '—';
  const paymentMethod = formatPaymentMethod(
    payment.payment_method || sub.payment_method || payment.provider
  );
  const lastPaymentDate = formatPaymentDate(
    payment.paid_at || sub.paid_at || sub.payment_date || sub.last_payment_at || sub.created_at
  );
  const invoiceUrl = sub.invoice_url || sub.invoice_link || sub.invoice_pdf_url || null;

  return {
    ...summary,
    planTitle,
    includedHeading: `What's included in your ${planTitle} plan`,
    features: parseSubscriptionFeatures(sub),
    classLabel,
    stream,
    subjects,
    paymentMethod,
    lastPaymentDate,
    invoiceUrl,
  };
}
